// src/pages/ScanToUSAPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import { apiUrl } from "../config/api";

// ---------- Types ----------
type Product = {
  code: string;
  name: string;
  unit?: string;
};

type ItemEntry = {
  product: Product;
  qty: number;
};

type FromLocation = "MAIN" | "SHOP" | "TIKTOK";

const FROM_LOCATIONS: FromLocation[] = ["MAIN", "SHOP", "TIKTOK"];

// PMN-CRITICAL(/ScanToUSAPage): shortage popup state — pre-check before sending USA batch when from_location stock < needed qty.
type ShortageItem = {
  product_code: string;
  product_name: string;
  needed: number;
  available_at_from: number;
  shortage: number;
  available_at_others: { code: FromLocation; available: number }[]; // exclude fromLocation + USA
  transferSource: FromLocation;
  transferStatus: "idle" | "loading" | "done" | "error";
  impossibleToFulfill: boolean; // sum of others < shortage
};

const TOKEN_STORAGE_KEY = "bg_shop_token";

function locationBadgeClass(code: string): string {
  switch (code) {
    case "MAIN": return "badge bg-primary";
    case "SHOP": return "badge bg-success";
    case "TIKTOK": return "badge bg-warning text-dark";
    default: return "badge bg-secondary";
  }
}

// PMN-CRITICAL(/ScanToUSAPage): scan/manual entry both feed addProductByCode; do NOT bypass fetchProduct cache.
export default function ScanToUSAPage(): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanLoopRef = useRef<number | null>(null);
  const gotOneRef = useRef<boolean>(false);

  const [reader] = useState(() => {
    const r = new BrowserMultiFormatReader();
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.CODE_128,
    ]);
    r.setHints?.(hints);
    return r;
  });

  const [scanning, setScanning] = useState(false);
  const [resultText, setResultText] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");

  const [fromLocation, setFromLocation] = useState<FromLocation>("SHOP");
  const [items, setItems] = useState<ItemEntry[]>([]);
  const [note, setNote] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  const [productsByCode, setProductsByCode] = useState<Map<string, Product>>(new Map());
  const [stockByCode, setStockByCode] = useState<Map<string, number>>(new Map());

  const [manualCode, setManualCode] = useState<string>("");
  const [showManual, setShowManual] = useState(false);
  const manualInputRef = useRef<HTMLInputElement | null>(null);

  const [toast, setToast] = useState<{ show: boolean; message: string; type?: string }>({
    show: false, message: "",
  });

  // PMN-CRITICAL(/ScanToUSAPage): Toast Undo — 30s window after successful batch transfer.
  // Replace strategy: new batch submission overwrites old undoableBill (timer auto-resets via useEffect dep).
  const [undoableBill, setUndoableBill] = useState<{ bill_no: string; expires_at: number } | null>(null);
  const [undoing, setUndoing] = useState(false);

  // Shortage popup state — see PMN
  const [shortages, setShortages] = useState<ShortageItem[]>([]);
  const [showShortagePopup, setShowShortagePopup] = useState(false);
  const [precheckLoading, setPrecheckLoading] = useState(false);

  // Toast auto-hide
  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast({ show: false, message: "" }), 2200);
    return () => clearTimeout(t);
  }, [toast.show]);

  // Undo timer — auto-clear when window expires
  useEffect(() => {
    if (!undoableBill) return;
    const ms = undoableBill.expires_at - Date.now();
    if (ms <= 0) { setUndoableBill(null); return; }
    const t = setTimeout(() => setUndoableBill(null), ms);
    return () => clearTimeout(t);
  }, [undoableBill]);

  // PMN-CRITICAL(/ScanToUSAPage): when scanning hits a code we MUST stop the loop + stream or scanner keeps re-firing the same code.
  const stopScanLoop = () => {
    if (scanLoopRef.current) cancelAnimationFrame(scanLoopRef.current);
    scanLoopRef.current = null;
    gotOneRef.current = false;
  };

  const stopStream = () => {
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  // beep
  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = 880;
      o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.2, ctx.currentTime);
      o.start();
      o.stop(ctx.currentTime + 0.12);
      setTimeout(() => ctx.close(), 300);
    } catch {}
  };

  // PMN-CRITICAL(/ScanToUSAPage): cache + canonical code (uppercase) before merging items, to avoid duplicate rows for case-variant codes.
  const fetchProduct = async (code: string): Promise<Product | null> => {
    setErrorMsg("");
    try {
      const key = code.trim().toUpperCase();
      const cached = productsByCode.get(key);
      if (cached) return cached;

      const url = new URL(apiUrl("/products"));
      url.searchParams.set("q", code);
      const res = await fetch(url.toString(), { headers: { Accept: "application/json" } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error ?? res.statusText);

      const items = Array.isArray(data?.items) ? data.items : [];
      const found =
        items.find((it: any) => String(it.code).toUpperCase() === key) ?? items[0];
      if (!found) return null;

      const p: Product = {
        code: String(found.code).toUpperCase(),
        name: String(found.name ?? ""),
        unit: found.unit ?? undefined,
      };
      setProductsByCode((prev) => {
        const next = new Map(prev);
        next.set(p.code, p);
        return next;
      });
      return p;
    } catch (e: any) {
      setErrorMsg(e?.message ?? "ดึงข้อมูลสินค้าไม่สำเร็จ");
      return null;
    }
  };

  // โหลด stock จาก fromLocation ของสินค้าตามต้องการ
  // PMN-NOTE(/ScanToUSAPage): preview only; backend /stock-transfers/usa-batch is the authoritative stock check.
  const fetchStockForCode = async (code: string): Promise<number> => {
    try {
      const resp = await fetch(apiUrl(`/stock-by-location/${encodeURIComponent(code)}/lots`));
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) return 0;
      const lots: any[] = Array.isArray(data?.items) ? data.items : [];
      return lots
        .filter((l) => String(l.location_code ?? "").toUpperCase() === fromLocation)
        .reduce((s, l) => s + Math.max(0, Number(l.available_qty ?? 0)), 0);
    } catch {
      return 0;
    }
  };

  const refreshStockForAll = async () => {
    const next = new Map(stockByCode);
    for (const it of items) {
      const qty = await fetchStockForCode(it.product.code);
      next.set(it.product.code, qty);
    }
    setStockByCode(next);
  };

  // Refresh stock when from_location changes
  useEffect(() => {
    if (items.length > 0) void refreshStockForAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromLocation]);

  const addProductByCode = async (rawCode: string) => {
    const input = rawCode.trim();
    if (!input) return;

    // PMN-NOTE(/ScanToUSAPage): digit-only shorthand → prepend "PD" (e.g. "1088" → "PD1088") so user can type just the number.
    const expandedInput = /^\d+$/.test(input) ? `PD${input}` : input;

    setResultText(expandedInput);
    playBeep();

    const p = await fetchProduct(expandedInput);
    if (!p) {
      setErrorMsg(`ไม่พบสินค้า code: ${expandedInput}`);
      return;
    }

    // fetch stock once
    const stockQty = await fetchStockForCode(p.code);
    setStockByCode((prev) => new Map(prev).set(p.code, stockQty));

    let toastName = p.name;
    let toastQty = 1;
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.product.code === p.code);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + 1 };
        toastName = copy[idx].product.name;
        toastQty = copy[idx].qty;
        return copy;
      }
      return [{ product: p, qty: 1 }, ...prev];
    });
    setToast({ show: true, message: `เพิ่ม ${toastName} x${toastQty}`, type: "success" });
  };

  const setQty = (idx: number, qty: number) => {
    const safe = Number.isFinite(qty) ? qty : 0;
    setItems((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], qty: Math.max(0, safe) };
      return copy;
    });
  };

  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  // ---------- Scanner ----------
  const startScan = async () => {
    setErrorMsg("");
    setResultText("");
    gotOneRef.current = false;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setScanning(true);
      runDecodeLoop();
    } catch (e: any) {
      setErrorMsg(e?.message ?? "เปิดกล้องไม่ได้ (ต้อง https หรือ localhost)");
      setScanning(false);
    }
  };

  const stopScan = () => {
    stopScanLoop();
    stopStream();
    setScanning(false);
  };

  const runDecodeLoop = () => {
    const video = videoRef.current;
    if (!video) return;
    const tick = async () => {
      if (gotOneRef.current) return;
      try {
        const result = await reader.decodeOnceFromVideoElement(video).catch(() => null);
        if (result && !gotOneRef.current) {
          gotOneRef.current = true;
          const code = result.getText();
          stopScanLoop();
          stopStream();
          setScanning(false);
          await addProductByCode(code);
          return;
        }
      } catch {}
      scanLoopRef.current = requestAnimationFrame(tick);
    };
    scanLoopRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    return () => {
      stopScanLoop();
      stopStream();
    };
  }, []);

  // PMN-CRITICAL(/ScanToUSAPage): pre-check shortage before USA batch. If any item qty > stock in fromLocation,
  // fetch lots across all locations, open shortage popup; user must transfer from MAIN/SHOP/TIKTOK (others) into fromLocation, then resubmit.
  const preCheckAndSubmit = async () => {
    if (items.length === 0) {
      setErrorMsg("ยังไม่มีรายการสินค้า");
      return;
    }
    if (items.some((it) => it.qty <= 0)) {
      setErrorMsg("จำนวนสินค้าทุกบรรทัดต้องมากกว่า 0");
      return;
    }

    setPrecheckLoading(true);
    setErrorMsg("");
    try {
      const shortageCandidates = items.filter(
        (it) => it.qty > (stockByCode.get(it.product.code) ?? 0)
      );
      if (shortageCandidates.length === 0) {
        await handleSubmit();
        return;
      }

      const otherLocations = FROM_LOCATIONS.filter((l) => l !== fromLocation);

      const shortageDetails: ShortageItem[] = await Promise.all(
        shortageCandidates.map(async (it) => {
          const haveFrom = stockByCode.get(it.product.code) ?? 0;
          const shortageQty = it.qty - haveFrom;

          let byLocation: Record<string, number> = {};
          try {
            const resp = await fetch(apiUrl(`/stock-by-location/${encodeURIComponent(it.product.code)}/lots`));
            const data = await resp.json().catch(() => ({}));
            const lots: any[] = Array.isArray(data?.items) ? data.items : [];
            for (const lot of lots) {
              const loc = String(lot.location_code ?? "").toUpperCase();
              byLocation[loc] = (byLocation[loc] ?? 0) + Math.max(0, Number(lot.available_qty ?? 0));
            }
          } catch {}

          const available_at_others = otherLocations.map((loc) => ({
            code: loc,
            available: byLocation[loc] ?? 0,
          }));
          const totalOther = available_at_others.reduce((s, x) => s + x.available, 0);
          const enough = available_at_others.find((x) => x.available >= shortageQty);
          const fallback = available_at_others.find((x) => x.available > 0);
          const defaultSource: FromLocation =
            enough?.code ?? fallback?.code ?? otherLocations[0];

          return {
            product_code: it.product.code,
            product_name: it.product.name,
            needed: it.qty,
            available_at_from: haveFrom,
            shortage: shortageQty,
            available_at_others,
            transferSource: defaultSource,
            transferStatus: "idle",
            impossibleToFulfill: totalOther < shortageQty,
          };
        })
      );

      setShortages(shortageDetails);
      setShowShortagePopup(true);
    } catch (e: any) {
      setErrorMsg(e?.message ?? "ตรวจ stock ไม่สำเร็จ");
    } finally {
      setPrecheckLoading(false);
    }
  };

  // PMN-CRITICAL(/ScanToUSAPage): per-row transfer to fromLocation; after success, refresh stockByCode so submit button re-evaluates.
  const transferShortageItem = async (idx: number) => {
    const s = shortages[idx];
    if (!s || s.transferStatus === "loading" || s.transferStatus === "done") return;
    if (s.impossibleToFulfill) return;
    const source = s.transferSource;
    const sourceAvail = s.available_at_others.find((x) => x.code === source)?.available ?? 0;
    if (sourceAvail < s.shortage) return;

    setShortages((prev) =>
      prev.map((x, i) => (i === idx ? { ...x, transferStatus: "loading" } : x))
    );
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl("/stock-transfers"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          product_code: s.product_code,
          from_location_code: source,
          to_location_code: fromLocation,
          qty: s.shortage,
          note: "auto-transfer for USA batch",
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.ok === false) {
        throw new Error(data?.error ?? "transfer_failed");
      }
      // refresh stock for this product in fromLocation
      const newStock = await fetchStockForCode(s.product_code);
      setStockByCode((prev) => new Map(prev).set(s.product_code, newStock));
      setShortages((prev) =>
        prev.map((x, i) => (i === idx ? { ...x, transferStatus: "done" } : x))
      );
    } catch (err: any) {
      setShortages((prev) =>
        prev.map((x, i) => (i === idx ? { ...x, transferStatus: "error" } : x))
      );
    }
  };

  const submitAfterShortageResolved = async () => {
    setShowShortagePopup(false);
    setShortages([]);
    await handleSubmit();
  };

  // ---------- Submit batch transfer ----------
  // PMN-CRITICAL(/ScanToUSAPage): batch POST is atomic; if backend fails (e.g. stock_not_enough on item #3), no item is transferred — safe to retry.
  const handleSubmit = async () => {
    if (items.length === 0) {
      setErrorMsg("ยังไม่มีรายการสินค้า");
      return;
    }
    if (items.some((it) => it.qty <= 0)) {
      setErrorMsg("จำนวนสินค้าทุกบรรทัดต้องมากกว่า 0");
      return;
    }
    if (submitting) return;

    setSubmitting(true);
    setErrorMsg("");
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl("/stock-transfers/usa-batch"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          from_location_code: fromLocation,
          note: note.trim() || undefined,
          items: items.map((it) => ({ product_code: it.product.code, qty: it.qty })),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const errKey = data?.error ?? resp.statusText;
        const meta = data?.meta;
        if (errKey === "stock_not_enough" && meta) {
          throw new Error(
            `สต็อก ${meta.product_code} ใน ${meta.from_location_code} ไม่พอ (ต้องการ ${meta.requested_qty}, มี ${meta.available_qty})`
          );
        }
        throw new Error(typeof errKey === "string" ? errKey : "failed_to_transfer");
      }
      setItems([]);
      setStockByCode(new Map());
      setNote("");
      setResultText("");
      setUndoableBill({ bill_no: String(data.bill_no), expires_at: Date.now() + 30_000 });
      setToast({
        show: true,
        message: `บันทึก ${data.bill_no} (${data.total_items} ชิ้น, ฿${Number(data.total_cost ?? 0).toFixed(2)})`,
        type: "success",
      });
    } catch (e: any) {
      setErrorMsg(e?.message ?? "ย้ายของไม่สำเร็จ");
    } finally {
      setSubmitting(false);
    }
  };

  // PMN-CRITICAL(/ScanToUSAPage): Toast Undo handler — fires POST /orders/:bill_no/undo-usa-transfer.
  // On success: clear undoableBill banner. On items_already_consumed: show meta.consumed_codes in message.
  const handleUndo = async (billNo: string) => {
    if (undoing) return;
    setUndoing(true);
    setErrorMsg("");
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl(`/orders/${encodeURIComponent(billNo)}/undo-usa-transfer`), {
        method: "POST",
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) {
        const err = data?.error ?? "failed_to_undo";
        if (err === "items_already_consumed") {
          const codes = (data?.meta?.consumed_codes ?? []).join(", ");
          throw new Error(`ของบาง lot ถูกใช้ต่อแล้ว (${codes}) ย้อนไม่ได้`);
        }
        if (err === "already_completed_cannot_undo") {
          throw new Error("บิลเสร็จสิ้นแล้ว ห้าม undo");
        }
        throw new Error(typeof err === "string" ? err : "failed_to_undo");
      }
      setUndoableBill(null);
      setToast({ show: true, message: `ย้อน ${billNo} เรียบร้อย`, type: "success" });
    } catch (e: any) {
      setErrorMsg(e?.message ?? "ย้อนกลับไม่สำเร็จ");
    } finally {
      setUndoing(false);
    }
  };

  // ---------- Render ----------
  return (
    <div className="min-vh-100 d-flex align-items-start justify-content-center bg-light">
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toast.show && (
          <div className={`alert alert-${toast.type ?? "success"} shadow mb-0 rounded-3 py-2 px-3`}>
            {toast.message}
          </div>
        )}
      </div>

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-8">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                  <h4 className="mb-0 text-danger">🇺🇸 สแกนย้ายของไป USA</h4>
                  <a href="/" className="btn btn-link text-decoration-none">← กลับเมนู</a>
                </div>

                {/* PMN-CRITICAL(/ScanToUSAPage): Undo banner — 30s window. Disappears via timer or after Undo success. */}
                {undoableBill && (
                  <div className="alert alert-warning d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                    <span className="small">
                      บันทึก <code>{undoableBill.bill_no}</code> แล้ว — กด Undo ภายใน <strong>30 วินาที</strong> เพื่อย้อนกลับ
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-warning"
                      disabled={undoing}
                      onClick={() => void handleUndo(undoableBill.bill_no)}
                    >
                      {undoing ? "กำลังย้อน…" : "↩ Undo"}
                    </button>
                  </div>
                )}

                {/* From location selector */}
                <div className="mb-3">
                  <label className="form-label">ย้ายมาจาก</label>
                  <div className="btn-group w-100" role="group">
                    {FROM_LOCATIONS.map((loc) => (
                      <button
                        key={loc}
                        type="button"
                        className={`btn ${fromLocation === loc ? "btn-dark" : "btn-outline-dark"}`}
                        onClick={() => setFromLocation(loc)}
                      >
                        <span className={locationBadgeClass(loc)}>{loc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Scanner */}
                <div className="mb-3 border rounded-3 p-3 bg-white">
                  <div className="d-flex gap-2 mb-2">
                    {!scanning ? (
                      <button className="btn btn-success" onClick={startScan}>
                        📷 เริ่มสแกน
                      </button>
                    ) : (
                      <button className="btn btn-danger" onClick={stopScan}>
                        ⏹ หยุดสแกน
                      </button>
                    )}
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => {
                        setShowManual((s) => !s);
                        setTimeout(() => manualInputRef.current?.focus(), 50);
                      }}
                    >
                      ⌨ พิมพ์โค้ดเอง
                    </button>
                  </div>
                  {scanning && (
                    <video
                      ref={videoRef}
                      playsInline
                      muted
                      style={{ width: "100%", maxHeight: 280, background: "#000", borderRadius: 8 }}
                    />
                  )}
                  {showManual && (
                    <div className="input-group mt-2">
                      <input
                        ref={manualInputRef}
                        type="text"
                        className="form-control"
                        placeholder="พิมพ์/วางรหัสสินค้า แล้วกด Enter"
                        value={manualCode}
                        onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            void addProductByCode(manualCode);
                            setManualCode("");
                          }
                        }}
                      />
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          void addProductByCode(manualCode);
                          setManualCode("");
                        }}
                      >
                        เพิ่ม
                      </button>
                    </div>
                  )}
                  {resultText && (
                    <div className="small text-muted mt-2">อ่านได้: <code>{resultText}</code></div>
                  )}
                </div>

                {errorMsg && (
                  <div className="alert alert-warning py-2 px-3 mb-3 small">{errorMsg}</div>
                )}

                {/* Items list */}
                {items.length === 0 ? (
                  <div className="text-center text-muted py-4 small">
                    ยังไม่มีสินค้า — สแกนหรือพิมพ์รหัสเพื่อเริ่ม
                  </div>
                ) : (
                  <div className="mb-3">
                    {items.map((entry, idx) => {
                      const stockHere = stockByCode.get(entry.product.code) ?? 0;
                      const overStock = entry.qty > stockHere;
                      return (
                        <div
                          key={`${entry.product.code}-${idx}`}
                          className={`card border rounded-3 shadow-sm mb-2 ${overStock ? "border-danger" : ""}`}
                        >
                          <div className="position-relative pb-2">
                            <button
                              type="button"
                              className="btn-close position-absolute"
                              style={{ top: 6, right: 6 }}
                              aria-label="Remove"
                              onClick={() => removeItem(idx)}
                            />
                          </div>
                          <div className="card-body py-3">
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <div>
                                <div className="small text-secondary">รหัสสินค้า</div>
                                <div className="fw-semibold text-primary">{entry.product.code}</div>
                              </div>
                              <div className="text-end">
                                <div className="small text-secondary">มีใน {fromLocation}</div>
                                <div className={`fw-bold ${overStock ? "text-danger" : "text-success"}`}>
                                  {stockHere.toLocaleString()}
                                </div>
                              </div>
                            </div>
                            <div className="fw-semibold text-dark mb-2">{entry.product.name}</div>
                            <div className="row g-2 align-items-center">
                              <div className="col-auto small text-secondary">จำนวน</div>
                              <div className="col-4 col-sm-3">
                                <input
                                  type="number"
                                  min={0}
                                  step={1}
                                  className={`form-control ${overStock ? "is-invalid" : ""}`}
                                  value={entry.qty}
                                  onChange={(e) => setQty(idx, Number(e.target.value))}
                                />
                              </div>
                              {entry.product.unit && (
                                <div className="col-auto small text-muted">หน่วย: {entry.product.unit}</div>
                              )}
                            </div>
                            {overStock && (
                              <div className="text-danger small mt-1">⚠ จำนวนเกินสต็อก</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Note */}
                <div className="mb-3">
                  <label className="form-label">หมายเหตุ (optional)</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    placeholder="เช่น TR สำหรับลูกค้า X"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>

                {/* Submit */}
                <div className="d-grid">
                  {/* PMN-CRITICAL(/ScanToUSAPage): preCheckAndSubmit handles shortage popup flow; allow press even when stock looks insufficient. */}
                  <button
                    type="button"
                    className="btn btn-danger btn-lg"
                    disabled={
                      submitting ||
                      precheckLoading ||
                      items.length === 0 ||
                      items.some((it) => it.qty <= 0)
                    }
                    onClick={() => void preCheckAndSubmit()}
                  >
                    {submitting
                      ? "กำลังย้าย…"
                      : precheckLoading
                        ? "ตรวจ stock…"
                        : `🇺🇸 ย้ายไป USA (${items.reduce((s, it) => s + it.qty, 0)} ชิ้น)`}
                  </button>
                </div>

                <div className="text-center small text-muted mt-3">
                  * ระบบจะสร้างบิล <code>USA-YYYYMMDD-xxxxx</code> ให้อัตโนมัติ (ต้นทุนล็อกตาม FIFO ของ {fromLocation})
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PMN-CRITICAL(/ScanToUSAPage): Shortage popup — block submit until each shortage row is transferred from a viable source location into fromLocation. */}
      {showShortagePopup && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.55)", zIndex: 3000 }}
        >
          <div className="card shadow-lg border-0 rounded-4" style={{ maxWidth: 560, width: "94%" }}>
            <div className="card-body p-4">
              <h5 className="mb-1 fw-bold">⚠️ Stock ใน {fromLocation} ไม่พอ</h5>
              <p className="text-muted small mb-3">ย้ายของจาก location อื่นเข้า {fromLocation} ก่อนส่งไป USA</p>

              <div className="d-flex flex-column gap-3 mb-3">
                {shortages.map((s, idx) => (
                  <div key={s.product_code} className="border rounded-3 p-3">
                    <div className="fw-semibold small">{s.product_code}</div>
                    <div className="small text-muted mb-2">{s.product_name}</div>
                    <div className="small mb-2">
                      มีใน {fromLocation} <strong>{s.available_at_from}</strong> / ต้องการ <strong>{s.needed}</strong>
                      {" → "}ขาด <strong className="text-danger">{s.shortage} ชิ้น</strong>
                    </div>

                    {s.impossibleToFulfill ? (
                      <div className="alert alert-danger small mb-0 py-2 px-3">
                        ❌ stock รวมของ {s.available_at_others.map((x) => x.code).join(" + ")} ไม่พอ ({s.available_at_others.reduce((sum, x) => sum + x.available, 0)} ชิ้น)
                        — กดย้ายไม่ได้ ต้องเพิ่ม stock ก่อน
                      </div>
                    ) : s.transferStatus === "done" ? (
                      <div className="text-success small fw-semibold">✅ ย้ายแล้ว {s.shortage} ชิ้น จาก {s.transferSource}</div>
                    ) : s.transferStatus === "error" ? (
                      <div className="text-danger small">❌ ย้ายไม่สำเร็จ ลองใหม่อีกครั้ง</div>
                    ) : (
                      <>
                        <div className="d-flex gap-3 mb-2">
                          {s.available_at_others.map((opt) => {
                            const disabled = opt.available < s.shortage;
                            return (
                              <label key={opt.code} className={`d-flex align-items-center gap-1 small ${disabled ? "text-muted" : ""}`}>
                                <input
                                  type="radio"
                                  name={`src-${s.product_code}`}
                                  value={opt.code}
                                  checked={s.transferSource === opt.code}
                                  disabled={disabled || s.transferStatus === "loading"}
                                  onChange={() =>
                                    setShortages((prev) =>
                                      prev.map((x, i) => (i === idx ? { ...x, transferSource: opt.code } : x))
                                    )
                                  }
                                />
                                {opt.code} (มี {opt.available}){disabled ? " — ไม่พอ" : ""}
                              </label>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          className="btn btn-warning btn-sm"
                          disabled={
                            s.transferStatus === "loading" ||
                            (s.available_at_others.find((x) => x.code === s.transferSource)?.available ?? 0) < s.shortage
                          }
                          onClick={() => void transferShortageItem(idx)}
                        >
                          {s.transferStatus === "loading"
                            ? "กำลังย้าย…"
                            : `ย้าย ${s.shortage} ชิ้น จาก ${s.transferSource} → ${fromLocation}`}
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>

              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={submitting}
                  onClick={() => {
                    setShowShortagePopup(false);
                    setShortages([]);
                  }}
                >
                  ยกเลิก
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={
                    submitting ||
                    !shortages.every((s) => s.transferStatus === "done")
                  }
                  onClick={() => void submitAfterShortageResolved()}
                >
                  {submitting
                    ? "กำลังบันทึก…"
                    : shortages.every((s) => s.transferStatus === "done")
                      ? "✅ บันทึกและย้ายไป USA"
                      : "บันทึก (ย้ายยังไม่ครบ)"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
